import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Icons, useNotification } from '@ohif/ui-next';
import loadingGif from '../../../assets/loading.gif';
import './ChangePasswordModal.css';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  isForced?: boolean; // When true, cancel and close buttons are hidden/disabled
}

const PASSWORD_POLICIES = [
  'Minimum 8 characters, maximum 30 characters',
  'At least one lowercase letter (a-z)',
  'At least one uppercase letter (A-Z)',
  'At least one number (0-9)',
  'At least one special character (!@#$%^&* etc.)',
];

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
  isForced = false,
}) => {
  const { changePassword } = useAuth();
  const notification = useNotification();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState<string | null>(null);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  const handleClose = () => {
    // Prevent closing if forced or if changing password
    if (isForced || isChangingPassword) {
      return;
    }
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setChangePasswordError(null);
    setChangePasswordSuccess(null);
    setErrors({});
    onClose();
  };

  const validateForm = (): boolean => {
    const newErrors: {
      currentPassword?: string;
      newPassword?: string;
      confirmPassword?: string;
    } = {};

    if (!currentPassword.trim()) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = 'Confirm password is required';
    } else if (newPassword && newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'New password and confirmation do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      setChangePasswordError(null);
      setChangePasswordSuccess(null);
      return;
    }

    try {
      setIsChangingPassword(true);
      setChangePasswordError(null);
      setChangePasswordSuccess(null);
      setErrors({});

      // Delegate to AuthService via context; it already calls:
      // POST /api/v1/users/change-password
      // payload: { curr_password, new_password }
      const result = await changePassword(currentPassword, newPassword);

      if (result.success) {
        const successMessage =
          result.message || 'Your password has been changed successfully.';

        setChangePasswordSuccess(successMessage);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setErrors({});

        // Show success toast
        notification.show({
          title: 'Password updated',
          message: successMessage,
          type: 'success',
          duration: 3000,
          position: 'top-right',
        });

        // Close the modal on success
        // For forced password change, onClose will show confirmation dialog
        if (!isForced) {
          handleClose();
        } else {
          // For forced password change, call onClose which will show confirmation
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setShowCurrentPassword(false);
          setShowNewPassword(false);
          setShowConfirmPassword(false);
          setChangePasswordError(null);
          setChangePasswordSuccess(null);
          setErrors({});
          // onClose will be called by parent to show confirmation dialog
          onClose();
        }
      } else {
        const errorMessage =
          result.message ||
          'Unable to change password. Please verify your current password and try again.';

        setChangePasswordError(errorMessage);

        // Show error toast
        notification.show({
          title: 'Password change failed',
          message: errorMessage,
          type: 'error',
          duration: 5000,
          position: 'top-right',
        });
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="change-password-modal-overlay">
      <div
        className="change-password-modal-content"
        onClick={e => e.stopPropagation()}
      >
        {isChangingPassword && (
          <div className="change-password-loading-overlay">
            <img
              src={loadingGif}
              alt="Loading..."
              className="change-password-loading-spinner"
            />
          </div>
        )}
        <div className="change-password-modal-header">
          <h3>Change Password</h3>
          {!isForced && (
            <button
              className="change-password-button change-password-button-secondary change-password-button-close"
              aria-label="Close change password"
              onClick={handleClose}
            >
              ×
            </button>
          )}
        </div>
        <form onSubmit={handleSubmit}>
          <div className="change-password-modal-body">
            <div className="change-password-form-grid">
              <div className="change-password-form-group">
                <label htmlFor="current-password">Current password</label>
                <div className="change-password-password-input-container">
                  <input
                    id="current-password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={e => {
                      setCurrentPassword(e.target.value);
                      if (errors.currentPassword) {
                        setErrors(prev => ({ ...prev, currentPassword: undefined }));
                      }
                    }}
                    className={errors.currentPassword ? 'error' : ''}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="change-password-password-toggle-btn"
                    onClick={() => setShowCurrentPassword(prev => !prev)}
                    aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                  >
                    {showCurrentPassword ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.currentPassword && (
                  <div className="change-password-error-message">
                    {errors.currentPassword}
                  </div>
                )}
              </div>

              <div className="change-password-form-group">
                <label
                  htmlFor="new-password"
                  className="change-password-form-label-with-help"
                >
                  <span>New password</span>
                  <span className="change-password-form-help-wrapper">
                    <button
                      type="button"
                      className="change-password-form-help-icon"
                      aria-label="Password requirements"
                    >
                      <Icons.Info className="change-password-form-help-icon-svg" />
                    </button>
                    <div
                      className="change-password-form-password-tooltip"
                      role="tooltip"
                    >
                      <div className="change-password-password-policy-title">
                        Password Policy:
                      </div>
                      <ul>
                        {PASSWORD_POLICIES.map(policy => (
                          <li key={policy}>{policy}</li>
                        ))}
                      </ul>
                    </div>
                  </span>
                </label>
                <div className="change-password-password-input-container">
                  <input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={e => {
                      setNewPassword(e.target.value);
                      if (errors.newPassword) {
                        setErrors(prev => ({ ...prev, newPassword: undefined }));
                      }
                      // Clear confirm password error if passwords match
                      if (errors.confirmPassword && e.target.value === confirmPassword) {
                        setErrors(prev => ({ ...prev, confirmPassword: undefined }));
                      }
                    }}
                    className={errors.newPassword ? 'error' : ''}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="change-password-password-toggle-btn"
                    onClick={() => setShowNewPassword(prev => !prev)}
                    aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                  >
                    {showNewPassword ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.newPassword && (
                  <div className="change-password-error-message">
                    {errors.newPassword}
                  </div>
                )}
              </div>

              <div className="change-password-form-group">
                <label htmlFor="confirm-password">Confirm new password</label>
                <div className="change-password-password-input-container">
                  <input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => {
                      setConfirmPassword(e.target.value);
                      if (errors.confirmPassword) {
                        setErrors(prev => ({ ...prev, confirmPassword: undefined }));
                      }
                    }}
                    className={errors.confirmPassword ? 'error' : ''}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="change-password-password-toggle-btn"
                    onClick={() => setShowConfirmPassword(prev => !prev)}
                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    {showConfirmPassword ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <div className="change-password-error-message">
                    {errors.confirmPassword}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="change-password-modal-footer">
            {!isForced && (
              <button
                type="button"
                className="change-password-button change-password-button-secondary"
                onClick={handleClose}
                disabled={isChangingPassword}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="change-password-button change-password-button-primary"
              disabled={isChangingPassword}
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
